import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ChevronDown, Lightbulb, Quote,
  TrendingUp, FileCheck2, Calculator,
} from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";
import { ABOUT_COPY } from "@/data/aboutCopy";

const SITE = "https://oceandata.ca";
const STORY_ICONS = { "story-construction": FileCheck2, "story-field": TrendingUp, "story-finance": Calculator };

const ROSTER = [
  [
    "Tremplin Marketing", "ITHG", "ARQ", "AHQ", "Université Concordia", "iGotcha",
    "Structure NCK", "Sketch Nanotechnologies", "Royal LePage Village",
    "Quartier des Spectacles", "Poly Industries", "Physiotec", "Petites-Mains",
    "OnPower", "SecurPlus", "Groupe Central", "AlumaSafway", "Aquatech BM",
    "AutoCarMatch", "B B Pallets", "Canada College", "Chemtec Coatings",
  ],
  [
    "Classy — Tenue de Gala Avanti", "Cobmex", "Enaircomp", "Congo Mining Services",
    "Credico Marketing", "Delight Canada", "Digitech Payments", "Étiquette Nationale",
    "Fanair Cosmetiques", "RE/MAX Québec", "Géant du Conteneur", "GenPak",
    "Groupe Focus", "Groupe RDC", "ImmoFacile", "InLibro", "Kristom Canada",
    "Kristom USA", "Leblanc Construction", "Multi-Recycle",
  ],
];

export default function AboutPage({ lang = "en" }) {
  const { setOpen } = useSimplify();
  const t = ABOUT_COPY[lang] || ABOUT_COPY.en;
  const [openExp, setOpenExp] = useState(0);

  useEffect(() => {
    document.title = t.docTitle;
    document.documentElement.lang = lang;
    const links = [];
    Object.entries(t.hreflangs).forEach(([hreflang, path]) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = `${SITE}${path}`;
      el.setAttribute("data-hreflang-injected", "true");
      document.head.appendChild(el);
      links.push(el);
    });
    return () => {
      document.documentElement.lang = "en";
      links.forEach((el) => el.remove());
    };
  }, [t, lang]);

  const personSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Person",
        name: "Bruno Pouliot",
        jobTitle: "Founder & Principal Consultant",
        worksFor: { "@type": "Organization", name: "Ocean Data", url: SITE },
        description:
          "The quiet specialist: 15+ years helping construction and professional-services companies remove friction from daily work through Zoho One, Zoho CRM, Zoho Projects, Zoho Books, and advanced Claude AI integrations.",
        knowsAbout: [
          "Zoho One", "Zoho CRM", "Zoho Projects", "Zoho Books",
          "Claude AI integrations", "Model Context Protocol", "Business process simplification", "Lean operations",
        ],
      },
      {
        "@type": "AboutPage",
        name: t.docTitle,
        inLanguage: t.schemaLang,
        url: `${SITE}${t.hreflangs[Object.keys(t.hreflangs).find((k) => (k === "en-CA" && lang === "en") || (k === "fr-CA" && lang === "fr") || (k === "es" && lang === "es"))]}`,
        mainEntity: { "@type": "Organization", name: "Ocean Data", url: SITE },
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang={lang}>
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="col-span-12 lg:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
              {t.eyebrow}
            </p>
            <h1
              data-testid="about-headline"
              className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl"
            >
              <MaskedLine delay={0.15} testId="about-headline-line-1">{t.h1a}</MaskedLine>
              <MaskedLine delay={0.3} testId="about-headline-line-2">
                <span className="text-brand-teal">{t.h1b}</span>
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
            >
              {t.heroSub}
            </motion.p>
            <motion.blockquote
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 border-l-2 border-brand-teal pl-6 font-display font-medium text-xl sm:text-2xl text-brand-navy leading-snug"
            >
              {t.heroQuote}
            </motion.blockquote>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-6 relative"
          >
            <div className="absolute -inset-3 rounded-[2rem] bg-brand-tealsoft rotate-1" />
            <img
              src="/bruno.jpg"
              alt="Bruno Pouliot, founder of Ocean Data"
              width="1440"
              height="1440"
              loading="lazy"
              decoding="async"
              className="relative rounded-[1.75rem] w-full h-[320px] sm:h-[420px] object-cover object-top shadow-[0_30px_60px_-20px_rgba(14,20,51,0.3)]"
            />
            <div className="absolute -bottom-5 left-6 sm:left-10 rounded-2xl bg-brand-navy text-white px-5 py-4 shadow-xl">
              <p className="font-display font-semibold text-sm leading-none">{t.photoName}</p>
              <p className="text-[11px] text-slate-300 mt-1">{t.photoCaption}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-4">
              {t.storyEyebrow}
            </p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl lg:text-4xl mb-8">
              {t.storyTitle}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-6 text-base sm:text-lg text-slate-600 leading-relaxed">
              {t.storyParas.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {t.chips.map((c) => (
                <span
                  key={c}
                  className="rounded-full bg-brand-tealsoft border border-brand-tealborder text-brand-navy text-xs font-semibold px-4 py-2"
                >
                  {c}
                </span>
              ))}
              <a
                href="https://www.linkedin.com/in/bruno-pouliot-5a992535/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-linkedin-link"
                className="rounded-full bg-brand-navy text-white text-xs font-semibold px-4 py-2 transition-colors duration-300 hover:bg-brand-teal"
              >
                {t.linkedin}
              </a>
            </div>
            <figure
              data-testid="about-certification-badge"
              className="mt-6 flex items-center gap-5 rounded-2xl border border-slate-200 bg-white p-5 max-w-lg"
            >
              <img
                src="/zoho-certified.jpg"
                alt="Zoho CRM Certified Administrator certificate — Bruno Pouliot"
                width="777"
                height="735"
                loading="lazy"
                decoding="async"
                className="w-24 h-24 object-contain rounded-lg border border-slate-100 bg-white"
              />
              <figcaption>
                <p className="font-display font-semibold text-sm text-brand-navy leading-snug">
                  {t.certTitle}
                </p>
                <p className="text-xs text-slate-500 leading-relaxed mt-1.5">
                  {t.certBody}
                </p>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* Capacity stories */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal className="mb-12">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-4">
              {t.storiesEyebrow}
            </p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
              {t.storiesTitle}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
            {t.stories.map((s, i) => {
              const Icon = STORY_ICONS[s.id];
              return (
                <Reveal key={s.id} delay={i * 0.08} className="h-full">
                  <div
                    data-testid={s.id}
                    className="h-full rounded-2xl bg-white border border-slate-200 p-7 sm:p-8 transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <span className="w-11 h-11 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center">
                        <Icon size={18} />
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">
                        {s.seat}
                      </span>
                    </div>
                    <h3 className="font-display font-semibold text-lg text-brand-navy leading-snug mb-3">
                      {s.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-6">{s.body}</p>
                    <p className="font-mono text-xs font-semibold text-brand-teal uppercase tracking-[0.1em]">
                      {s.returned}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-5 text-xs text-slate-400">
              {t.storiesFootnote}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Client roster */}
      <section data-testid="client-roster" className="pb-20 lg:pb-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 mb-10">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-4">
              {t.rosterEyebrow}
            </p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl max-w-2xl">
              {t.rosterTitle}
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="space-y-4 select-none" aria-label="Client roster">
            {ROSTER.map((row, ri) => (
              <div key={ri} className="overflow-hidden">
                <div
                  className="od-marquee-track flex w-max"
                  style={ri === 1 ? { animationDirection: "reverse" } : undefined}
                >
                  {[0, 1].map((dup) => (
                    <div key={dup} className="flex shrink-0 items-center" aria-hidden={dup === 1}>
                      {row.map((name) => (
                        <span key={`${ri}-${dup}-${name}`} className="flex items-center">
                          <span className="font-display font-semibold text-lg sm:text-xl text-slate-400 whitespace-nowrap px-6 sm:px-8 hover:text-brand-teal transition-colors duration-300">
                            {name}
                          </span>
                          <span className="text-brand-gold text-xs">✦</span>
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <p className="text-center text-xs text-slate-400 max-w-md mx-auto pt-6 px-4">
              {t.rosterCaption}
            </p>
          </div>
        </Reveal>
      </section>

      {/* Expertise */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-12 gap-8 lg:gap-12">
          <Reveal className="col-span-12 lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-4">
              {t.expertiseEyebrow}
            </p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl mb-5">
              {t.expertiseTitle}
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              {t.expertiseBody}
            </p>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 lg:col-span-7 lg:col-start-6">
            <div data-testid="about-expertise-list" className="space-y-3">
              {t.expertise.map((e, i) => (
                <div
                  key={e.q}
                  className={`rounded-2xl border bg-white overflow-hidden transition-colors duration-300 ${openExp === i ? "border-brand-teal" : "border-slate-200"}`}
                >
                  <button
                    data-testid={`about-expertise-toggle-${i}`}
                    onClick={() => setOpenExp(openExp === i ? -1 : i)}
                    aria-expanded={openExp === i}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-display font-medium text-base sm:text-lg text-brand-navy">{e.q}</span>
                    <motion.span animate={{ rotate: openExp === i ? 180 : 0 }} transition={{ duration: 0.3 }}>
                      <ChevronDown size={17} className="text-brand-teal shrink-0" />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {openExp === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p data-testid={`about-expertise-content-${i}`} className="px-6 pb-6 text-sm sm:text-base text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                          {e.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Made to Stick */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div data-testid="about-stick-panel" className="rounded-3xl bg-brand-tealsoft border border-brand-tealborder p-8 sm:p-10">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-teal font-semibold mb-6">
                <Lightbulb size={14} /> {t.stickTitle}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {t.stick.map(([label, value], i) => (
                  <div key={i} className="rounded-xl bg-white/70 border border-brand-tealborder/60 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-teal font-semibold mb-2">{label}</p>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Closing */}
      <section className="bg-brand-navy text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-12 gap-8 items-center">
          <Reveal className="col-span-12 lg:col-span-7">
            <Quote size={22} className="text-brand-teal mb-5" />
            <p className="font-display font-medium text-xl sm:text-2xl leading-snug mb-4 max-w-xl">
              {t.closeQuote}
            </p>
            <p className="text-sm text-slate-400 mb-7">{t.closeAuthor}</p>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {t.closeLinks.map((l) => (
                <Link key={l.to} to={l.to} className="text-sm font-semibold text-brand-gold hover:text-white transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 lg:col-span-5 flex lg:justify-end">
            <button
              data-testid="about-cta-button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
            >
              {t.closeCta}
              <ArrowRight size={18} />
            </button>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }} />
    </main>
  );
}
